import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Account = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  const [status, setStatus] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .get(`${BACKEND_URL}/api/telegram/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ enabled: false }));
  }, [isAuthenticated]);

  const generateCode = async () => {
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/link-code`);
      setLinkCode(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  const handleUnlink = async () => {
    setError('');
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/telegram/unlink`);
      setStatus((s) => ({ ...(s || {}), linked: false, alerts_enabled: false }));
      setLinkCode(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#06070d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f2e2a6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#06070d] via-[#0b0e1a] to-[#06070d]">
      <Navbar />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2">Telegram bot</h1>
          <p className="text-gray-400 mb-8">
            Link your account to get alerts and reach support straight in Telegram.
          </p>

          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            {error && (
              <div
                className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm mb-4"
                data-testid="account-error"
              >
                {error}
              </div>
            )}

            {!status ? (
              <div className="text-gray-400 text-sm">Loading…</div>
            ) : !status.enabled ? (
              <div className="text-gray-400 text-sm" data-testid="account-bot-disabled">
                The Telegram bot isn't available yet — check back soon.
              </div>
            ) : !status.plan_allows_bot ? (
              <div data-testid="account-plan-gate">
                <p className="text-gray-300 mb-4">
                  Telegram bot access is included in the <span className="text-[#f2e2a6]">Pro</span> and{' '}
                  <span className="text-[#f2e2a6]">Advanced</span> plans.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold"
                >
                  View plans
                </Button>
              </div>
            ) : status.linked ? (
              <div data-testid="account-linked">
                <div className="flex items-center gap-2 mb-4">
                  <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
                  <span className="text-white font-medium">Connected</span>
                </div>
                <p className="text-gray-400 text-sm mb-6">
                  Alerts are currently{' '}
                  <span className="text-white">{status.alerts_enabled ? 'on' : 'off'}</span> — toggle with{' '}
                  <code className="text-[#f2e2a6]">/alerts on|off</code> in the chat.
                </p>
                <Button
                  onClick={handleUnlink}
                  disabled={busy}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  data-testid="account-unlink"
                >
                  {busy ? 'Unlinking…' : 'Unlink this chat'}
                </Button>
              </div>
            ) : linkCode ? (
              <div data-testid="account-link-code">
                <p className="text-gray-400 text-sm mb-4">
                  Open Telegram and send this to the bot, or use the button below. Expires in{' '}
                  {linkCode.expires_in_minutes} minutes.
                </p>
                <div className="bg-black/40 border border-white/10 rounded-lg p-4 mb-4 text-center">
                  <code className="text-[#f2e2a6] text-xl tracking-widest">/start {linkCode.code}</code>
                </div>
                {linkCode.deep_link && (
                  <a href={linkCode.deep_link} target="_blank" rel="noreferrer">
                    <Button className="w-full bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold">
                      Open in Telegram
                    </Button>
                  </a>
                )}
              </div>
            ) : (
              <div data-testid="account-not-linked">
                <p className="text-gray-400 text-sm mb-6">Your Telegram isn't linked yet.</p>
                <Button
                  onClick={generateCode}
                  disabled={busy}
                  className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold"
                  data-testid="account-generate-code"
                >
                  {busy ? 'Generating…' : 'Get link code'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Account;
